import { ResultAsync } from "neverthrow";
import type { ZodType } from "zod";
import z from "zod";

import {
  buildInternalError,
  getErrorMessage,
  type InternalError,
  type InvalidError,
} from "@thalamos/common";

import { canonicalise } from "./canonical.js";

/** The one algorithm, named in the value so a second could be told from it. */
const ALGORITHM = "sha256";

/** Why a document has no digest: it has no canonical spelling, or crypto failed. */
export type DigestError = InvalidError | InternalError;

/**
 * What a published schema is called: the digest of its canonical spelling.
 *
 * Web Crypto rather than Node's, because this package is bundled for the
 * browser as well as run on the server, and a digest is the same fact either
 * side. It is asynchronous for that reason and no other.
 *
 * The digest covers the schema *and* its `$id`, so two instruments cannot
 * collide by having the same shape under different names.
 */
export const digestOf = (document: unknown): ResultAsync<string, DigestError> =>
  canonicalise(document)
    .asyncAndThen((canonical) =>
      ResultAsync.fromPromise(
        globalThis.crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(canonical),
        ),
        (cause) =>
          buildInternalError({
            message: "The document could not be digested",
            issues: [getErrorMessage(cause)],
          }),
      ),
    )
    .map(
      (hash) =>
        `${ALGORITHM}:${[...new Uint8Array(hash)]
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("")}`,
    );

/** Whether a string is shaped like a digest this produces. */
export const isDigest = (value: string): boolean =>
  new RegExp(`^${ALGORITHM}:[0-9a-f]{64}$`).test(value);

export type InstrumentDigestUnbranded = string;
export const InstrumentDigestSchema = z
  .string()
  .refine(isDigest, "Not a digest")
  .brand<"haven-common:InstrumentDigest">() satisfies ZodType<InstrumentDigestUnbranded>;
/**
 * What names one published instrument version: the digest of its schema's
 * canonical spelling, which a caller fetches the schema by and a pinned
 * instrument carries. Branded apart from other digests, such as that of a
 * presentation entry, which share its shape but name something else.
 */
export type InstrumentDigest = z.infer<typeof InstrumentDigestSchema>;
